import { APIService } from "shared/services";
import { API_ENDPOINTS, API_ERROR_CODES } from "shared/constants";
import { IDataKeys, IJSONAPIItem, IAPIError } from "shared/types";
import {
  IGuestlistTicketAndPassParams,
  IGuestlistBlockParams,
  IEventGuestParams,
} from "Bands/types";
import { merge, range } from "lodash-es";

interface IJSONAPIGuestlistBlock {
  [key: string]: IJSONAPIItem;
}

interface IJSONAPIGuestlistBlockCollection {
  [IDataKeys.eventGuestListBlock]: IJSONAPIGuestlistBlock;
  meta: {
    [key: string]: IJSONAPIItem;
  };
  pagination: any;
}

interface IJSONAPITicketAndPassCollection {
  [IDataKeys.eventGuestListTicketAndPassType]: IJSONAPIGuestlistBlock;
  meta: {
    [key: string]: IJSONAPIItem;
  };
  pagination: any;
}

interface IJSONAPIGuestCollection {
  [IDataKeys.eventGuestListBlockEntry]: IJSONAPIGuestlistBlock;
  meta: {
    [key: string]: IJSONAPIItem;
  };
  pagination: any;
}

export class EventGuestlistBlockService {
  static getBlocks = (
    queryParams?: string | { [key: string]: string | number | undefined | string[] }
  ): Promise<IJSONAPIGuestlistBlockCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const blocks: unknown = await APIService.get({
          path: API_ENDPOINTS.EVENT_GUESTLIST_BLOCKS,
          data: queryParams,
        });

        resolve(blocks as IJSONAPIGuestlistBlockCollection);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.GET_EVENT_GUESTLIST_BLOCKS);
        return reject(error);
      }
    });

  static getBlock = (id: string): Promise<IJSONAPIGuestlistBlockCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.get({
          path: API_ENDPOINTS.EVENT_GUESTLIST_BLOCK(id),
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.GET_EVENT_GUESTLIST_BLOCK);
        return reject(error);
      }
    });

  static createBlock = (data: IGuestlistBlockParams): Promise<IJSONAPIGuestlistBlockCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.post({
          path: API_ENDPOINTS.EVENT_GUESTLIST_BLOCKS,
          data,
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.POST_EVENT_GUESTLIST_BLOCK);
        return reject(error);
      }
    });

  static editBlockTitle = ({
    id,
    title,
  }: {
    id: string;
    title: string;
  }): Promise<IJSONAPIGuestlistBlock> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.patch({
          path: API_ENDPOINTS.EVENT_GUESTLIST_BLOCK(id),
          data: { title },
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.PATCH_EVENT_GUESTLIST_BLOCK);
        return reject(error);
      }
    });

  static editBlock = ({
    id,
    data,
  }: {
    id: string;
    data: IGuestlistBlockParams;
  }): Promise<IJSONAPIGuestlistBlockCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.patch({
          path: API_ENDPOINTS.EVENT_GUESTLIST_BLOCK(id),
          data,
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.PATCH_EVENT_GUESTLIST_BLOCK);
        return reject(error);
      }
    });

  static deleteBlock = (id: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.delete({
          path: API_ENDPOINTS.EVENT_GUESTLIST_BLOCK(id),
        });
        resolve();
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.DELETE_EVENT_GUESTLIST_BLOCK);
        return reject(error);
      }
    });

  static createTicketAndPass = (
    data: IGuestlistTicketAndPassParams
  ): Promise<IJSONAPITicketAndPassCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.post({
          path: API_ENDPOINTS.EVENT_TICKET_PASSES,
          data,
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.POST_EVENT_TICKET_PASS);
        return reject(error);
      }
    });

  static editTicketAndPass = ({
    id,
    data,
  }: {
    id: string;
    data: IGuestlistTicketAndPassParams;
  }): Promise<IJSONAPITicketAndPassCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.patch({
          path: API_ENDPOINTS.EVENT_TICKET_PASS(id),
          data,
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.PATCH_EVENT_TICKET_PASS);
        return reject(error);
      }
    });

  static deleteTicketAndPass = (id: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.delete({
          path: API_ENDPOINTS.EVENT_TICKET_PASS(id),
        });
        resolve();
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.DELETE_EVENT_TICKET_PASS);
        return reject(error);
      }
    });

  static getGuests = (
    queryParams?: {
      [key: string]: any;
    },
    isRecursive?: boolean
  ): Promise<IJSONAPIGuestCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        let guests = await APIService.get({
          path: API_ENDPOINTS.EVENT_GUESTS,
          data: queryParams,
        });

        if (!isRecursive) {
          resolve(guests as IJSONAPIGuestCollection);
          return;
        }

        // get all guests until last page
        const { currentPage, totalPages } = guests.pagination || {};
        const toFetchPages = range(currentPage + 1, totalPages + 1);

        for (const page of toFetchPages) {
          const newGuest = await APIService.get({
            path: API_ENDPOINTS.EVENT_GUESTS,
            data: {
              ...queryParams,
              page,
            },
          });

          guests = merge({}, guests, newGuest);
        }

        guests.pagination.currentPage = 1 as any;

        resolve(guests as IJSONAPIGuestCollection);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.GET_EVENT_GUESTS);
        return reject(error);
      }
    });

  static createGuest = (data: IEventGuestParams): Promise<IJSONAPIGuestCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.post({
          path: API_ENDPOINTS.EVENT_GUESTS,
          data,
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.POST_EVENT_GUEST);
        return reject(error);
      }
    });

  static editGuest = ({
    id,
    data,
  }: {
    id: string;
    data: IEventGuestParams;
  }): Promise<IJSONAPIGuestCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.patch({
          path: API_ENDPOINTS.EVENT_GUEST(id),
          data,
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.PATCH_EVENT_GUEST);
        return reject(error);
      }
    });

  static deleteGuest = (id: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.delete({
          path: API_ENDPOINTS.EVENT_GUEST(id),
        });
        resolve();
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.DELETE_EVENT_GUEST);
        return reject(error);
      }
    });

  static downloadBlock = (id: string, printNote?: string): Promise<{ pdfUrl: string }> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.post({
          path: API_ENDPOINTS.DOWNLOAD_EVENT_GUESTLIST_BLOCK(id),
          isJSON: true,
          normalizeData: false,
          data: { printNote },
        });

        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.DOWNLOAD_EVENT_GUESTLIST_BLOCK);
        return reject(error);
      }
    });
}
